import { Button, IconButton, InputAdornment, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link, useNavigate } from "react-router-dom";
import GoogleLoginButton from "../components/login-with-google.component";
import StyledTextField from "../components/styled-text-field.compoment";
import config from "../config";
import logo from "../images/logo-with-caption.webp";
import { _post } from "../services/api.service";
import "../styles/login.css";
import { COLORS } from "../theme/theme.config";
import { Validation } from "../validation/validation";
import { getRecaptchaToken } from "../utils/recaptcha";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

export interface Field {
  value?: string | boolean;
  error?: string;
}

export interface Form {
  email: Field;
  password: Field;
  confirmPassword: Field;
}

export interface FormValidation {
  email: Validation.Validator[];
  password: Validation.Validator[];
}

const CreateAccountForm = ({
  executeRecaptcha,
}: {
  executeRecaptcha?: (action?: string) => Promise<string>;
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [form, setForm] = useState<Form>({} as any);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setEmail("");
    setPassword("");
    setForm({} as Form);
    setShowPassword(false);
  }, []);

  const formValidation: FormValidation = {
    email: [
      {
        validatorType: Validation.ValidationType.REQUIRED,
        message: "This field is required",
      },
      {
        validatorType: Validation.ValidationType.EMAIL,
        message: "Invalid email format",
      },
    ],
    password: [
      {
        validatorType: Validation.ValidationType.REQUIRED,
        message: "This field is required",
      },
      {
        validatorType: Validation.ValidationType.MIN,
        message: "Your password must be longer than 8 characters",
        props: {
          min: 8,
        },
      },
    ],
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const updateForm = (
    key: string,
    validators: Validation.Validator[],
    value: string | boolean
  ) => {
    const validationData = Validation.validate(value, validators);
    const errors: Validation.Validator[] = validationData.errors;
    setForm((prevForm) => ({
      ...prevForm,
      [key]: {
        value,
        error: errors.length > 0 ? errors[0].message : "",
      },
    }));
  };

  const onEmailChange = (e: any) => {
    const email = e.target.value;
    setEmail(email);
    sessionStorage.setItem("USER_EMAIL", email);
    updateForm("email", formValidation.email, email);
  };

  const onPasswordChange = (e: any) => {
    const password = e.target.value;
    setPassword(password);
    updateForm("password", formValidation.password, password);
  };

  const handleCreateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailErrors = Validation.validate(email, formValidation.email).errors;
    const passwordErrors = Validation.validate(password, formValidation.password).errors;

    setForm({
      email: {
        value: email,
        error: emailErrors[0]?.message ?? "",
      },
      password: {
        value: password,
        error: passwordErrors[0]?.message ?? "",
      },
      confirmPassword: {},
    });

    if (emailErrors.length > 0 || passwordErrors.length > 0) {
      return;
    }

    const token = await getRecaptchaToken(executeRecaptcha, "create_account");
    if (!token) {
      return;
    }

    const payload = {
      email,
      password,
      recaptcha: token,
    };

    const user = await _post("users/accounts/", payload);

    if (user) {
      navigate("/activate-account");
    }
  };

  return (
    <div className="outer">
      <div className="middle">
        <div className="login_form">
          <div className="mb-4">
            <img src={logo} alt="Logo" style={{ height: "40px" }} />
          </div>
          <div className="content-container">
            <h1 className="login-heading">Create Your Account</h1>
            <div className="login-description">
              <p>
              Please use the same email address you used when signing up.
              </p>
            </div>
          </div>
          <div style={{ margin: "10px 0 20px", textAlign: "center" }}>
            <GoogleLoginButton></GoogleLoginButton>
          </div>
          <div
            style={{
              textAlign: "center",
              margin: "0 0 10px",
              color: "#808080",
              fontSize: "12px",
            }}
          >
            OR
          </div>
          <form onSubmit={handleCreateUser} autoComplete="off">
            <div className="mb-3">
              <StyledTextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                autoFocus
                required
                value={email}
                onChange={onEmailChange}
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
              />
              <div className="error-container">
                <p className="error">{form.email?.error}</p>
              </div>
            </div>
            <div className="mb-4">
              <StyledTextField
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Password"
                variant="outlined"
                required
                color="secondary"
                value={password}
                onChange={onPasswordChange}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{
                          color: "#808080",
                        }}
                        onClick={togglePasswordVisibility}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <RemoveRedEyeOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className="error-container">
                <p className="error">{form.password?.error}</p>
              </div>
            </div>

            <Button
              sx={{
                backgroundColor: COLORS.green,
                padding: "14px",
                borderRadius: "0",
                fontSize: "1rem",
                color: "#09091c",
                "&:hover": {
                  backgroundColor: COLORS.greenHover,
                },
                "&:disabled": {
                  backgroundColor: COLORS.greenDisabled,
                  color: "#09091c",
                },
              }}
              fullWidth
              variant="contained"
              type="submit"
            >
              Create Account
            </Button>

            <p className="sign_up mt-4">
              Already have an account?
              <Link
                style={{
                  marginLeft: "10px",
                  color: COLORS.green,
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
                to="/login"
              >
                Login
              </Link>
            </p>
          </form>
        </div>
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            bottom: 10,
            width: "100%",
            textAlign: "center",
            fontSize: 13,
            color: "#9da7b2",
            px: 2,
          }}
        >
          By signing up with theunplan.com, you agree to our{" "}
          <a
            href="https://theunplan.com/tnc"
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#626876",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            terms of service
          </a>
          .
        </Typography>
      </div>
    </div>
  );
};

const CreateAccountWithRecaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return <CreateAccountForm executeRecaptcha={executeRecaptcha} />;
};

const CreateAccount = () => {
  if (process.env.REACT_APP_VERIFY_RECAPTCHA === "false") {
    return <CreateAccountForm />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.captchaKey}>
      <CreateAccountWithRecaptcha />
    </GoogleReCaptchaProvider>
  );
};

export default CreateAccount;
